import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeploymentUnitOutlined, ReloadOutlined } from '@ant-design/icons';
import { listEntities } from '@/api/entities';
import type { EntitySummary } from '@/api/entities';
import { normalizeApiError, isNotAvailable } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';

const { Text } = Typography;

function statusColor(s: string): string {
  switch (s) {
    case 'active':
      return 'success';
    case 'merged':
      return 'processing';
    case 'redacted':
      return 'warning';
    case 'tombstoned':
      return 'default';
    default:
      return 'default';
  }
}

function ts(v?: string | null): string {
  return v ? new Date(v).toLocaleString() : '—';
}

export function EntitiesPage() {
  usePageTitle('Entities');
  const navigate = useNavigate();
  const [items, setItems] = useState<EntitySummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState<string | undefined>();
  const [idPrefix, setIdPrefix] = useState('');
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listEntities({ type, idPrefix: idPrefix || undefined, page, pageSize: 25 });
      setItems(res.items);
      setTotal(res.totalCount);
      setUnavailable(false);
      // accrete the seen type_keys as filter options (no dedicated endpoint; the browse IS the source)
      setTypes((prev) => Array.from(new Set([...prev, ...res.items.map((i) => i.typeKey)])).sort());
    } catch (e) {
      const err = normalizeApiError(e);
      if (isNotAvailable(err)) setUnavailable(true);
      else {
        setError(err.message);
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [type, idPrefix, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<EntitySummary> = [
    {
      title: 'Entity',
      key: 'id',
      render: (_, r) => (
        <Text className="ol-mono" style={{ fontSize: 12 }}>
          {r.id}
        </Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'typeKey',
      key: 'type',
      width: 200,
      render: (t: string) => <Tag bordered={false}>{t}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s: string) => (
        <Tag bordered={false} color={statusColor(s)}>
          {s}
        </Tag>
      ),
    },
    { title: 'Valid from', key: 'validFrom', width: 200, render: (_, r) => ts(r.validFrom) },
    { title: 'First seen', key: 'sysFrom', width: 200, render: (_, r) => ts(r.sysFrom) },
  ];

  if (unavailable) {
    return (
      <EmptyState
        icon={<DeploymentUnitOutlined />}
        title="Entity graph"
        description="The entity graph is not available in this deployment."
        bullets={[]}
      />
    );
  }

  return (
    <div className="ol-page">
      <PageHeader
        title="Entities"
        icon={<DeploymentUnitOutlined />}
        subtitle="Every subject this hub holds in the bitemporal graph. Scan by type, open a row for the entity-360 — relationships, UBO traversal, evidence, and the as-of point-in-time view. The list carries identifiers only; attributes stay behind the 360 read."
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          placeholder="All types"
          style={{ width: 220 }}
          value={type}
          onChange={(v) => {
            setPage(1);
            setType(v);
          }}
          options={types.map((t) => ({ value: t, label: t }))}
        />
        <Input.Search
          placeholder="Entity id starts with…"
          allowClear
          style={{ width: 260 }}
          onSearch={(v) => {
            setPage(1);
            setIdPrefix(v.trim());
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>
          Refresh
        </Button>
      </Space>
      <Table<EntitySummary>
        size="small"
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={items}
        loading={loading && items.length > 0}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading entities" />
          ) : error ? (
            <ErrorState what="this hub's entities" detail={error} onRetry={() => void load()} compact />
          ) : (
            <EmptyState
              compact
              icon={<DeploymentUnitOutlined />}
              title="No entities match"
              description="Nothing in the graph matches these filters. Entities arrive when a flow run resolves a subject, when a case is opened against one, or through an entity-graph write."
            />
          ),
        }}
        onRow={(r) => ({
          onClick: () => navigate(`/entities/${encodeURIComponent(r.id)}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: 25,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />
    </div>
  );
}
